import { get, writable } from 'svelte/store';
import type { MenuListDataType } from '../../components/Profiles/DropdownItem.types';
import {
	mapProfilesForNative,
	notifyNativeProfiles,
	shouldSyncToNative,
} from 'src/helpers/native_bridge_helpers';
import { user_data } from '../account/user';

export const showProfileModal = writable<boolean>(false);

export const getProfileAvatarText = (title?: string, email?: string): string => {
	return (title || email || '').slice(0, 1).toUpperCase();
};

function createMultipleProfilesStore() {
	const { subscribe, set, update } = writable<MenuListDataType[]>(
		JSON.parse(localStorage.getItem('profiles')) || [],
	);

	/**
	 * Validation of avatar object with correct types.
	 */
	const cleanAvatar = (avatar: MenuListDataType['acc_avatar'] | undefined) => {
		if (!avatar) return {};

		const cleanedAvatar = { ...avatar };

		if (cleanedAvatar?.image && typeof cleanedAvatar.image !== 'string') {
			delete cleanedAvatar.image;
		}

		if (cleanedAvatar?.backgroundColor && typeof cleanedAvatar.backgroundColor !== 'string') {
			delete cleanedAvatar.backgroundColor;
		}

		return cleanedAvatar;
	};

	return {
		subscribe,
		set,
		update,
		add: (newProfile: MenuListDataType) =>
			update((profiles) => {
				const sanitizedProfile = {
					...newProfile,
					acc_avatar: {
						...cleanAvatar(newProfile.acc_avatar),
						text: getProfileAvatarText(newProfile.title, newProfile.email),
					},
				};

				const existingIndex = profiles.findIndex((item) => item.email === newProfile.email);

				if (existingIndex !== -1) {
					return profiles.map((item, i) => {
						if (i === existingIndex) {
							return {
								...item,
								...sanitizedProfile,
								isSelected: true,
							};
						}
						return {
							...item,
							isSelected: false,
						};
					});
				}

				const updatedProfiles = profiles.map((item) => {
					return {
						...item,
						isSelected: false,
					};
				});

				return [sanitizedProfile, ...updatedProfiles];
			}),
		change: (email: string) => {
			update((profiles) => {
				const updatedProfiles = profiles
					.map((item) => {
						if (email === item.email) {
							return {
								...item,
								isSelected: true,
							};
						}
						return {
							...item,
							isSelected: false,
						};
					})
					.sort((a, b) => Number(b.isSelected) - Number(a.isSelected));
				return [...updatedProfiles];
			});
		},
		edit: (editedProfile: MenuListDataType) => {
			update((profiles) => {
				const updatedProfiles = profiles.map((item) => {
					if (editedProfile.email === item.email) {
						return {
							...item,
							...editedProfile,
							acc_avatar: {
								...cleanAvatar(editedProfile.acc_avatar),
								text: getProfileAvatarText(editedProfile.title, editedProfile.email),
							},
						};
					}
					return item;
				});
				return [...updatedProfiles];
			});
		},
		remove: (email: string) => {
			update((profiles) => {
				const removedWasSelected = profiles.some(
					(item) => item.email === email && item.isSelected,
				);
				const filteredProfiles = profiles.filter((item) => item.email !== email);

				if (!removedWasSelected) {
					return [...filteredProfiles];
				}

				return filteredProfiles.map((item, i) => ({
					...item,
					isSelected: i === 0,
				}));
			});
		},
		reset: () => {
			return [];
		},
	};
}

export const profiles = createMultipleProfilesStore();

const notifyNativeProfilesFromStores = () => {
	const session = get(user_data);
	notifyNativeProfiles(
		mapProfilesForNative(get(profiles), {
			email: session.email,
			installer: session.installer,
		}),
	);
};

profiles.subscribe((val) => {
	localStorage.setItem('profiles', JSON.stringify(val));
	notifyNativeProfilesFromStores();
});
